import { ianaTimeZoneNameSchema } from "@redotech/redo-common-model/timezone-list";
import { unaryRpc } from "@redotech/rpc/definition";
import { yyyyMmDdSchema } from "@redotech/zod-util/db-coercion";
import * as z from "zod";

const MAX_AGENT_ACTIVITY_RANGE_DAYS = 366;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const humanQueueAgentActivityInputSchema = z
  .object({
    startAt: z.date(),
    endAt: z.date(),
    timeZone: ianaTimeZoneNameSchema,
  })
  .refine(({ startAt, endAt }) => startAt < endAt, {
    message: "startAt must be before endAt",
    path: ["startAt"],
  })
  .refine(
    ({ startAt, endAt }) =>
      endAt.getTime() - startAt.getTime() <=
      MAX_AGENT_ACTIVITY_RANGE_DAYS * MILLISECONDS_PER_DAY,
    {
      message: `Time range cannot exceed ${MAX_AGENT_ACTIVITY_RANGE_DAYS} days.`,
      path: ["endAt"],
    },
  );

const humanQueueAgentActivityAgentSchema = z.object({
  adminUserId: z.string(),
  email: z.email(),
  totalSent: z.number().int().nonnegative(),
  defaultQueueSent: z.number().int().nonnegative(),
  defaultQueueModifiedSent: z.number().int().nonnegative(),
  flaggedQueueSent: z.number().int().nonnegative(),
  flaggedQueueModifiedSent: z.number().int().nonnegative(),
  modifiedSent: z.number().int().nonnegative(),
});

const humanQueueAgentDailySendsSchema = z.object({
  adminUserId: z.string(),
  date: yyyyMmDdSchema,
  sent: z.number().int().nonnegative(),
});

export const getHumanQueueAgentActivity = unaryRpc({
  name: "getHumanQueueAgentActivity" as const,
  description:
    "Get human queue send activity by admin user for an exact timestamp range, grouped by time zone.",
  input: humanQueueAgentActivityInputSchema,
  output: z.object({
    agents: z.array(humanQueueAgentActivityAgentSchema),
    dailySends: z.array(humanQueueAgentDailySendsSchema),
  }),
});

export type GetHumanQueueAgentActivityInput = z.infer<
  typeof humanQueueAgentActivityInputSchema
>;

export type GetHumanQueueAgentActivityOutput = z.infer<
  typeof getHumanQueueAgentActivity.output
>;
